import {
  EmailAuthProvider,
  linkWithCredential,
  linkWithPopup,
  reauthenticateWithCredential,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  updatePassword,
  type Auth,
  type User as FirebaseUser,
} from 'firebase/auth';
import {
  doc,
  serverTimestamp,
  updateDoc,
  type Firestore,
} from 'firebase/firestore';

import { auth, db, googleProvider } from '@/lib/firebase';
import { WORKER_API_BASE_URL } from '@/lib/worker-api';

export type UsernameResolver = (identifier: string) => Promise<{ credentialEmail: string }>;

const API_BASE = WORKER_API_BASE_URL;
const ADMIN_REQUEST_TIMEOUT_MS = 15000;

type ApiErrorBody = {
  error?: string;
  code?: string;
  message?: string;
};

export type AuthorizedRequestError = Error & {
  status?: number;
  code?: string;
};

export async function resolveUsername(identifier: string): Promise<{ credentialEmail: string }> {
  if (identifier.includes('@')) {
    return { credentialEmail: identifier.trim() };
  }

  let response: Response;
  try {
    response = await fetch(`${API_BASE}/api/auth/username/resolve`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username: identifier }),
    });
  } catch {
    throw Object.assign(new Error('Username resolver unavailable'), {
      code: 'username/resolver-unavailable',
    });
  }

  if (!response.ok) {
    let errorBody: ApiErrorBody = {};
    try {
      errorBody = await response.json() as ApiErrorBody;
    } catch {
      errorBody = {};
    }
    const isServiceFailure = response.status >= 500;
    throw Object.assign(
      new Error(errorBody.message || 'Username resolver failed'),
      {
        code: isServiceFailure
          ? (errorBody.error || errorBody.code || 'username/resolver-unavailable')
          : 'username/not-found',
        status: response.status,
      },
    );
  }

  return response.json() as Promise<{ credentialEmail: string }>;
}

export async function signInWithUsernameAndPassword(
  identifier: string,
  password: string,
  resolve: UsernameResolver = resolveUsername,
  authInstance: Auth = auth,
) {
  const { credentialEmail } = await resolve(identifier);
  return signInWithEmailAndPassword(authInstance, credentialEmail, password);
}

function isGenericResetError(error: unknown): boolean {
  const code = (error as { code?: string })?.code;
  return code === 'username/not-found'
    || code === 'auth/user-not-found'
    || code === 'auth/invalid-email';
}

function isResolverServiceFailure(error: unknown): boolean {
  const status = (error as { status?: number })?.status;
  const code = (error as { code?: string })?.code;
  return (typeof status === 'number' && status >= 500)
    || code === 'LOCAL_AUTH_UNAVAILABLE'
    || code === 'username/resolver-unavailable';
}

export async function sendPasswordResetForIdentifier(
  identifier: string,
  resolve: UsernameResolver = resolveUsername,
  authInstance: Auth = auth,
): Promise<{ requested: true }> {
  let credentialEmail: string;
  try {
    ({ credentialEmail } = await resolve(identifier));
  } catch (error) {
    if (isResolverServiceFailure(error)) {
      throw error;
    }
    return { requested: true };
  }

  try {
    await sendPasswordResetEmail(authInstance, credentialEmail, {
      url: typeof window !== 'undefined' ? `${window.location.origin}/auth` : '/auth',
    });
  } catch (error) {
    if (!isGenericResetError(error)) {
      throw error;
    }
  }

  return { requested: true };
}

export async function changeOwnPassword(
  currentPassword: string,
  newPassword: string,
  authInstance: Auth = auth,
): Promise<void> {
  const currentUser = authInstance.currentUser;
  if (!currentUser?.email) {
    throw Object.assign(new Error('A signed-in password user is required.'), {
      code: 'auth/not-authenticated',
    });
  }
  const credential = EmailAuthProvider.credential(currentUser.email, currentPassword);
  await reauthenticateWithCredential(currentUser, credential);
  await updatePassword(currentUser, newPassword);
}

export async function completeRequiredPasswordChange(
  currentPassword: string,
  newPassword: string,
  authInstance: Auth = auth,
  dbInstance: Firestore = db,
): Promise<{ passwordResetRequired: false }> {
  const currentUser = authInstance.currentUser;
  if (!currentUser?.email) {
    throw Object.assign(new Error('A signed-in password user is required.'), {
      code: 'auth/not-authenticated',
    });
  }

  const credential = EmailAuthProvider.credential(currentUser.email, currentPassword);
  await reauthenticateWithCredential(currentUser, credential);
  await updatePassword(currentUser, newPassword);
  await updateDoc(doc(dbInstance, 'users', currentUser.uid), {
    password_reset_required: false,
    updated_at: serverTimestamp(),
    updated_by: currentUser.uid,
  });

  return { passwordResetRequired: false };
}

export async function linkGoogleProviderToCurrentUser(user: FirebaseUser) {
  return linkWithPopup(user, googleProvider);
}

export async function linkPasswordProviderToCurrentUser(
  user: FirebaseUser,
  email: string,
  password: string,
) {
  const credential = EmailAuthProvider.credential(email, password);
  return linkWithCredential(user, credential);
}

export async function linkProfile(): Promise<{ customToken?: string; linked: boolean; uid: string }> {
  const token = await auth.currentUser?.getIdToken();
  if (!token) {
    throw Object.assign(new Error('Not authenticated'), { code: 'auth/not-authenticated' });
  }
  const response = await fetch(`${API_BASE}/api/auth/link-profile`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw Object.assign(new Error(body.message || 'Profile linking failed'), {
      status: response.status,
      code: body.error || 'LINK_PROFILE_FAILED',
    });
  }
  return response.json();
}

export async function updateProviders(providerIds: string[], passwordLoginEnabled?: boolean): Promise<void> {
  const token = await auth.currentUser?.getIdToken();
  if (!token) {
    throw Object.assign(new Error('Not authenticated'), { code: 'auth/not-authenticated' });
  }
  const response = await fetch(`${API_BASE}/api/auth/update-providers`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ providerIds, passwordLoginEnabled }),
  });
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw Object.assign(new Error(body.message || 'Failed to update providers'), {
      status: response.status,
      code: body.error || 'UPDATE_PROVIDERS_FAILED',
    });
  }
}

async function authorizedJson(path: string, body: unknown) {
  const token = await auth.currentUser?.getIdToken();
  if (!token) {
    throw Object.assign(new Error('Not authenticated'), { code: 'auth/not-authenticated' });
  }

  const controller = new AbortController();
  const timeoutId = window.setTimeout(() => controller.abort(), ADMIN_REQUEST_TIMEOUT_MS);
  let response: Response;
  try {
    response = await fetch(`${API_BASE}${path}`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
      signal: controller.signal,
    });
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') {
      throw Object.assign(new Error('Request timed out'), { code: 'REQUEST_TIMEOUT' });
    }
    throw error;
  } finally {
    window.clearTimeout(timeoutId);
  }

  if (!response.ok) {
    let errorBody: ApiErrorBody = {};
    try {
      errorBody = await response.json() as ApiErrorBody;
    } catch {
      errorBody = {};
    }
    throw Object.assign(
      new Error(errorBody.message || `Request failed with ${response.status}`),
      {
        status: response.status,
        code: errorBody.error || errorBody.code,
      },
    );
  }

  return response.json();
}

export function createAdminPasswordUser(body: unknown) {
  return authorizedJson('/api/auth/admin/users', body);
}

export function adminUpdateUserProfile(uid: string, body: unknown) {
  return authorizedJson(`/api/auth/admin/users/${uid}/profile`, body);
}

export function adminSetUserPassword(uid: string, newPassword: string) {
  return authorizedJson(`/api/auth/admin/users/${uid}/set-password`, { newPassword });
}

export function deactivateUser(uid: string) {
  return authorizedJson(`/api/auth/admin/users/${uid}/deactivate`, {});
}

export function setPasswordResetRequired(uid: string) {
  return authorizedJson(`/api/auth/admin/users/${uid}/password-reset-required`, {});
}

export function adminDeleteUser(uid: string) {
  return authorizedJson(`/api/auth/admin/users/${uid}/delete`, {});
}
